import { useMemo, useRef, useState } from 'react'
import type { Navigate } from '../App'
import type { InputMode } from '../types'
import { useIsland, useSettings, useStore } from '../state/store'
import { useRecorder } from '../state/useRecorder'
import type { GradeResult } from '../lib/grade'
import { gradeFor, inputClass, languageOf, resolveMode } from '../lib/languages'
import { scoreClass } from '../lib/analysis'
import { GradeReport } from '../components/GradeReport'
import { SpeakButton } from '../components/SpeakButton'

/**
 * Untimed production practice, block by block. You see only the English, and
 * have to produce the Chinese from memory in whichever script you are drilling.
 */
export function RecallDrill({ id, navigate }: { id: string; navigate: Navigate }) {
  const island = useIsland(id)
  const settings = useSettings()
  const { dispatch } = useStore()
  const record = useRecorder()

  const [index, setIndex] = useState(0)
  const [attempt, setAttempt] = useState('')
  const [result, setResult] = useState<GradeResult | null>(null)
  const [scores, setScores] = useState<number[]>([])
  const [revealed, setRevealed] = useState(false)
  const startedAt = useRef(Date.now())

  const lang = languageOf(island?.language)
  const mode = resolveMode(lang.code, settings.inputMode)
  const block = island?.blocks[index]

  const averageSoFar = useMemo(
    () => (scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null),
    [scores],
  )

  if (!island || !block) {
    return (
      <div className="empty">
        Nothing to drill.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Back to islands
        </button>
      </div>
    )
  }

  const isLast = index === island.blocks.length - 1

  const check = () => {
    if (!attempt.trim()) return
    setResult(gradeFor(lang.code, block, attempt, mode, settings.requireTones))
  }

  const next = () => {
    if (!result) return
    const collected = [...scores, result.score]

    if (isLast) {
      const average = collected.reduce((a, b) => a + b, 0) / collected.length
      record({
        islandId: island.id,
        mode: 'recall',
        score: average,
        seconds: (Date.now() - startedAt.current) / 1000,
        inputMode: mode,
        result,
      })
      navigate({ view: 'island', id: island.id })
      return
    }

    setScores(collected)
    setIndex((i) => i + 1)
    setAttempt('')
    setResult(null)
    setRevealed(false)
  }

  return (
    <>
      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate({ view: 'island', id: island.id })}
        style={{ marginBottom: '0.8rem' }}
      >
        ← Leave drill
      </button>

      <header className="page-head">
        <div className="eyebrow">
          Recall · {island.title} · block {index + 1} of {island.blocks.length}
          {averageSoFar !== null && ` · running ${averageSoFar}`}
        </div>
        <h1 className="page-title">
          <span className="page-title-zh">{lang.ui.recall}</span> Produce it
        </h1>
      </header>

      <div className="beat-dots" aria-hidden="true">
        {island.blocks.map((b, i) => (
          <span
            key={b.id}
            className={`beat-dot ${i < index ? 'is-done' : ''} ${i === index ? 'is-current' : ''}`}
          />
        ))}
      </div>

      <div className="stage">
        <div className="stage-cue">Say this in {lang.name}</div>
        <p style={{ fontSize: '1.3rem', margin: '0 0 0.8rem' }}>{block.english}</p>

        {revealed ? (
          <div className="appear">
            <div className={lang.code === 'zh' ? 'hanzi' : ''} style={{ fontSize: '1.5rem' }}>
              {block.hanzi}
            </div>
            {lang.hasPhonetics && <div className="pinyin">{block.pinyin}</div>}
          </div>
        ) : (
          <button type="button" className="btn is-quiet is-small" onClick={() => setRevealed(true)}>
            Reveal — counts as a miss you can see coming
          </button>
        )}
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: '0.7rem' }}>
          {lang.modes.length > 1 && (
          <div className="toggle-group" role="group" aria-label="Input mode">
            {(lang.modes as InputMode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={`btn is-small ${mode === m ? 'is-on' : ''}`}
                onClick={() => dispatch({ type: 'updateSettings', patch: { inputMode: m } })}
              >
                {m === 'hanzi' ? '汉字' : m === 'pinyin' ? 'pīnyīn' : lang.name}
              </button>
            ))}
          </div>
          )}

          {mode === 'pinyin' && (
            <label className="checkline">
              <input
                type="checkbox"
                checked={settings.requireTones}
                onChange={(e) =>
                  dispatch({ type: 'updateSettings', patch: { requireTones: e.target.checked } })
                }
              />
              <span className="muted" style={{ fontSize: '0.86rem' }}>
                Tones required
              </span>
            </label>
          )}

          <span className="muted" style={{ fontSize: '0.8rem', marginLeft: 'auto' }}>
            {mode === 'hanzi' ? 'Type with your Chinese IME' : lang.inputHint}
          </span>
        </div>

        <input
          className={`answer-input ${inputClass(mode)}`}
          value={attempt}
          autoFocus
          lang={mode === 'pinyin' ? 'en' : lang.speech}
          placeholder={mode === 'pinyin' ? 'type the pinyin…' : lang.answerPlaceholder}
          onChange={(e) => setAttempt(e.target.value)}
          onKeyDown={(e) => {
            // Enter checks, then Enter again advances — but never while an IME
            // is mid-composition, or the first candidate selection submits.
            if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
            e.preventDefault()
            if (result) next()
            else check()
          }}
          disabled={Boolean(result)}
        />

        <div className="row" style={{ marginTop: '0.8rem' }}>
          {!result ? (
            <button
              type="button"
              className="btn is-primary"
              onClick={check}
              disabled={!attempt.trim()}
            >
              Check
            </button>
          ) : (
            <button type="button" className="btn is-primary" onClick={next}>
              {isLast ? 'Finish island' : 'Next block'}
            </button>
          )}
          <SpeakButton text={block.hanzi} language={lang.code} label="Play" className="btn" withSlow />
          {result && (
            <span className={`score ${scoreClass(result.score)}`} style={{ fontSize: '1.4rem', marginLeft: 'auto' }}>
              {result.score}
            </span>
          )}
        </div>
      </div>

      {result && (
        <GradeReport result={result} mode={mode} target={block} language={lang.code} showAudio={false} />
      )}
    </>
  )
}
